import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import {
    Meditation,
    MeditationAudioUpload,
    MeditationDraft,
    MeditationFilters,
    MeditationLicense,
    MeditationLikeState,
    MeditationPlayback,
    MeditationRecipient,
} from "../../domain/types";

function toParams(filters: MeditationFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters.kind) params.kind = filters.kind;
    if (filters.therapyType) params.therapy_type = filters.therapyType;
    if (filters.presentingProblem)
        params.presenting_problem = filters.presentingProblem;
    if (filters.language) params.language = filters.language;
    if (filters.search) params.search = filters.search;
    if (filters.author) params.author = filters.author;
    if (filters.order) params.order = filters.order;
    return params;
}

export const MeditationsAPI = {
    list: (
        filters: MeditationFilters = {}
    ): Promise<AxiosResponse<Meditation[]>> =>
        apiClient.get("/meditations", { params: toParams(filters) }),

    listPublic: (
        filters: MeditationFilters = {},
        page: { offset?: number; limit?: number } = {}
    ): Promise<AxiosResponse<Meditation[]>> =>
        apiClient.get("/meditations/public", {
            params: { ...toParams(filters), ...page },
        }),

    /** The library of the caller's centre. Empty for a therapist in none. */
    listCenter: (
        filters: MeditationFilters = {}
    ): Promise<AxiosResponse<Meditation[]>> =>
        apiClient.get("/meditations/center", { params: toParams(filters) }),

    get: (id: string): Promise<AxiosResponse<Meditation>> =>
        apiClient.get(`/meditations/${id}`),

    create: (draft: MeditationDraft): Promise<AxiosResponse<Meditation>> =>
        apiClient.post("/meditations", draft),

    update: (
        id: string,
        changes: Partial<MeditationDraft>
    ): Promise<AxiosResponse<Meditation>> =>
        apiClient.patch(`/meditations/${id}`, changes),

    remove: (id: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/meditations/${id}`),

    publish: (
        id: string,
        payload: { author_name?: string | null; license?: MeditationLicense }
    ): Promise<AxiosResponse<Meditation>> =>
        apiClient.post(`/meditations/${id}/publish`, payload),

    unpublish: (id: string): Promise<AxiosResponse<Meditation>> =>
        apiClient.delete(`/meditations/${id}/publish`),

    copy: (id: string): Promise<AxiosResponse<Meditation>> =>
        apiClient.post(`/meditations/${id}/copy`),

    like: (id: string): Promise<AxiosResponse<MeditationLikeState>> =>
        apiClient.post(`/meditations/${id}/like`),

    unlike: (id: string): Promise<AxiosResponse<MeditationLikeState>> =>
        apiClient.delete(`/meditations/${id}/like`),

    /** Where to load the recording from, for as long as the link lasts. */
    /** The meditation's own bed, for a script read live with no recording. */
    background: (id: string): Promise<AxiosResponse<MeditationPlayback>> =>
        apiClient.get(`/meditations/${id}/background`),

    /**
     * The recording's bytes, through our own API.
     *
     * The bucket does not answer browsers — no CORS headers on a signed URL —
     * and the session needs the bytes rather than a link: it publishes what it
     * plays into the room through the Web Audio API, which cannot capture a
     * cross-origin element.
     */
    audio: (id: string): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/meditations/${id}/audio`, { responseType: "blob" }),

    backgroundAudio: (id: string): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/meditations/${id}/background-audio`, {
            responseType: "blob",
        }),

    playback: (id: string): Promise<AxiosResponse<MeditationPlayback>> =>
        apiClient.get(`/meditations/${id}/playback`),

    /**
     * A slot to upload a recording into. Available before the meditation
     * exists — a therapist records first and names it afterwards at least as
     * often as the other way round.
     */
    audioUploadUrl: (payload: {
        content_type: string;
        meditation_id?: string;
    }): Promise<AxiosResponse<MeditationAudioUpload>> =>
        apiClient.post("/meditations/audio-upload-url", payload),

    /**
     * Who has this meditation in their own portal.
     *
     * Sharing is per patient rather than a switch on the meditation: the same
     * body scan goes to several people, and taking it back from one of them
     * must not take it from the rest.
     */
    recipients: (id: string): Promise<AxiosResponse<MeditationRecipient[]>> =>
        apiClient.get(`/meditations/${id}/patients`),

    share: (id: string, patientId: string): Promise<AxiosResponse<void>> =>
        apiClient.put(`/meditations/${id}/patients/${patientId}`),

    unshare: (id: string, patientId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/meditations/${id}/patients/${patientId}`),
};

/**
 * Push the recording straight to storage.
 *
 * Deliberately `fetch` rather than the app's Axios client: the signed URL is
 * GCS's, not ours, and the client attaches an Authorization header that GCS
 * rejects the request for.
 */
export async function uploadMeditationAudio(
    uploadUrl: string,
    blob: Blob
): Promise<void> {
    const response = await fetch(uploadUrl, {
        // The type has to be the one the URL was signed for, and the slot was
        // asked for without the codec parameter `MediaRecorder` adds — sending
        // it here would be a different header than the signature covers.
        headers: { "Content-Type": blob.type.split(";")[0].trim() },
        method: "PUT",
        body: blob,
    });
    if (!response.ok) {
        throw new Error(`Audio upload failed with status ${response.status}`);
    }
}
